import { useQuery } from '@tanstack/react-query'
import { geoApi, legislatorApi } from '../lib/api'
import type { DistrictUnitType } from '../components/lobbyDay/districtCatalog'
import type { Chamber } from '../types/lobbyDay'

export interface DistrictProbeInput {
  chamber: Chamber
  district: string
  state: string
}

/** District catalogue for a chamber's boundary type in one state, at the vintage the SERVER
 *  resolves. There is deliberately no vintage parameter here — a client-side vintage literal is
 *  an explicit "do NOT" in the geo feature card. Boundaries change only at redistricting, so the
 *  cache is long-lived. */
export function useGeoDistricts(type: DistrictUnitType | null, state: string) {
  const normalizedState = state.trim().toUpperCase()
  return useQuery({
    queryKey: ['geo', 'districts', type, normalizedState],
    queryFn: async () => (await geoApi.getDistricts(type!, normalizedState)).data,
    enabled: !!type && normalizedState.length === 2,
    staleTime: 60 * 60 * 1000,
  })
}

/** Constituent coverage for a batch of districts. The probe list is the query key, so an
 *  edited district refetches; callers must dedupe and cap before calling. */
export function useConstituentCounts(probes: DistrictProbeInput[], enabled: boolean) {
  return useQuery({
    queryKey: ['legislators', 'constituent-counts', probes],
    queryFn: async () => (await legislatorApi.constituentCounts(probes)).data,
    enabled: enabled && probes.length > 0,
  })
}
